import {
  AppBar,
  Collapse,
  CssBaseline,
  Drawer as DrawerMaterial,
  List,
  ListItem,
  ListItemIcon,
  Toolbar,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import clsx from 'clsx';
import jwtDecode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BaseText } from '..';
import { useMobile } from '../../hooks/use-mobile';
import LogoWhite from '../../images/logo/new_logo_fretapp.svg';
import { ApplicationState } from '../../store';
import { loginFailure } from '../../store/ducks/auth/actions';
import { loginDistributorFailure } from '../../store/ducks/distributor/actions';
import { setCurrentDelivery } from '../../store/ducks/tables/actions';
import { DrawerType } from '../../types/drawer.types';
import './styles.scss';

import {
  DRAWER_ICON,
  DRAWER_MENU,
  LIST_COLLAPSE_MENU,
  LIST_MENU,
  LIST_MENU_COMPANY,
} from './constants';
import IconSettings from '../../assets/icons/icon-settings';
import IconSignOut from '../../assets/icons/icon-sign-out';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      marginLeft: DRAWER_MENU,
      width: `calc(100% - ${DRAWER_MENU}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    menuButton: {
      marginRight: 'auto',
    },
    hide: {
      display: 'none',
    },
    drawerPaper: {
      width: DRAWER_MENU,
    },
    drawer: {
      width: DRAWER_MENU,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    drawerOpen: {
      width: DRAWER_MENU,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: theme.spacing(7) + 1,
      [theme.breakpoints.up('sm')]: {},
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(3, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
    },
    notificationIcon: {
      marginRight: 24,
    },
    popover: {
      padding: theme.spacing(2),
      minWidth: 240,
    },
  }),
);

export const Drawer: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { deliveries } = useSelector((state: ApplicationState) => state.tables);
  const token = useSelector(
    (state: ApplicationState) => state.auth.token || state.distributor.token,
  );

  const actualRoute = window.location.pathname.substring(1).split('/')[1];
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMobile();
  const [isAdmin, setIsAdmin] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [openCollapseMenu, setOpenCollapseMenu] = useState(false);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const checkUserAdmin = () => {
    const data: any = jwtDecode(token);

    setIsAdmin(data.role === 'admin');
  };

  const handleOpenSubMenu = () => {
    setOpenSubMenu(!openSubMenu);
  };

  const handleOpenCollapseMenu = () => {
    setOpenCollapseMenu(!openCollapseMenu);
  };

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
      setMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    checkUserAdmin();
  }, []);

  useEffect(() => {
    if (deliveries.length !== 0) {
      dispatch(setCurrentDelivery(deliveries[0]));
    }
  }, [deliveries]);

  const handleDrawerToggle = (type: DrawerType) => {
    if (type === DrawerType.Mobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const goLogout = () => {
    if (isAdmin) {
      dispatch(loginFailure({}));
      history.push('/admin/login');
    } else {
      dispatch(loginDistributorFailure({}));
      history.push('/login');
    }
  };

  const onDrawerClick = (routeName: string) => {
    history.push(`/home/${routeName}`);
    setMobileOpen(false);
  };

  const drawer = (
    <>
      <div className={classes.toolbar}>
        {(mobileOpen || open) && <img src={LogoWhite} alt="logo" />}
      </div>

      <List className="container-list">
        {(isAdmin ? LIST_MENU : LIST_MENU_COMPANY).map(
          // TODO: adjust this
          (item, index) => (
            <ListItem
              button
              key={index.toString()}
              onClick={() => onDrawerClick(item.routerName)}
              selected={actualRoute === item.routerName}
            >
              <ListItemIcon>{DRAWER_ICON[item.icon]}</ListItemIcon>
              <BaseText.BodyBold>{item.name}</BaseText.BodyBold>
              {item.subName && (
                <BaseText.BodyBold>{` ${item.subName}`}</BaseText.BodyBold>
              )}
            </ListItem>
          ),
        )}

        {isAdmin && (
          <ListItem button onClick={handleOpenSubMenu}>
            <ListItemIcon>
              <IconSettings />
            </ListItemIcon>
            <BaseText.BodyBold>Configurações</BaseText.BodyBold>
            {openSubMenu ? (
              <ExpandLess style={{ color: '#fff' }} />
            ) : (
              <ExpandMore style={{ color: '#fff' }} />
            )}
          </ListItem>
        )}
        <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {LIST_COLLAPSE_MENU.map((item, index) => (
              <ListItem
                button
                key={index.toString()}
                onClick={() => onDrawerClick(item.routerName)}
                selected={actualRoute === item.routerName}
              >
                <ListItemIcon>{DRAWER_ICON[item.icon]}</ListItemIcon>
                <BaseText.BodyBold>{item.name}</BaseText.BodyBold>
              </ListItem>
            ))}
          </List>
        </Collapse>
        <Collapse in={openCollapseMenu} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {LIST_COLLAPSE_MENU.map((item, index) => (
              <ListItem
                button
                key={index.toString()}
                onClick={() => onDrawerClick(item.routerName)}
                selected={actualRoute === item.routerName}
              >
                <ListItemIcon>{DRAWER_ICON[item.icon]}</ListItemIcon>
                <BaseText.BodyBold>{item.name}</BaseText.BodyBold>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>

      <ListItem button onClick={goLogout}>
        <ListItemIcon>
          <IconSignOut />
        </ListItemIcon>
        <BaseText.BodyBold>Sair</BaseText.BodyBold>
      </ListItem>
    </>
  );

  const handleNotificationClick = (event: any) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const openNotification = Boolean(notificationAnchor);

  const appBar = (
    <AppBar
      position="fixed"
      className={clsx(classes.appBar, {
        [classes.appBarShift]: open,
      })}
    >
      <Toolbar style={{ margin: '0 25px' }}>
        
        <div style={{ flexGrow: '1' }} />
        {/* <IconButton>
          <img src={IconUser} alt="User Icon" />
        </IconButton>
        <IconButton>
          <img src={IconNotification} alt="Notification Icon" />
        </IconButton> */}
      </Toolbar>
    </AppBar>
  );

  return (
    <div className="container-drawer">
      <CssBaseline />
      {appBar}
      {!isMobile ? (
        <DrawerMaterial
          variant="permanent"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
        >
          {drawer}
        </DrawerMaterial>
      ) : (
        <DrawerMaterial
          className="container-drawer"
          open={mobileOpen}
          onClose={() => handleDrawerToggle(DrawerType.Mobile)}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawer}
        </DrawerMaterial>
      )}
    </div>
  );
};

export default Drawer;
